import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axiosConn from 'config/axiosConn2';
import { Container, Table, Spinner, Alert, Button, Row, Col, Badge } from 'react-bootstrap';
import { FaDownload, FaSchool, FaInbox } from "react-icons/fa";
import HeaderAdmin from '../../HeaderAdmin';
import Pagination from './Pagination';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { IoMdArrowRoundBack } from "react-icons/io";
import * as XLSX from 'xlsx';
import './ViewSchools.css';

function ViewSchools() {
  const location = useLocation();
  const { state, district, theme } = location.state || {};
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 12;

  const fetchSchools = async (page = 1) => {
    if (!state || !district) {
      setError('State or district not provided.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = theme === 'environment-day'
        ? `environment-day-schools?page=${page}&limit=${limit}`
        : `get-school-lists?page=${page}&limit=${limit}`;

      const res = await axiosConn('post', url, { state, district });
      if (res.data.status === 'success') {
        setSchools((res.data.schools || []).filter(s => s.school_name));
        setCurrentPage(res.data.currentPage || page);
        setTotalPages(res.data.totalPages || 1);
      } else {
        setError('Failed to fetch schools.');
      }
    } catch (err) {
      setError('Failed to fetch schools.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSchools(currentPage);
    // eslint-disable-next-line
  }, [state, district, currentPage]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Download as PDF
  const handleDownloadPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFontSize(18);
    doc.setTextColor(30, 126, 52);
    doc.text(`Schools Participation Report - ${district}, ${state}`, 14, 16);

    const tableColumn = [
      "School Name",
      "UDISE ID",
      "District",
      "School Type",
      "Total Pledges",
      "Teachers",
      "Students"
    ];

    const tableRows = schools.map(school => [
      school.school_name,
      school.udise_id || school.udiseCode,
      district,
      school.school_type || 'N/A',
      school.totalPledges || 0,
      school.teachersCount || 0,
      school.studentsCount || 0
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 24,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [30, 126, 52], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [245, 250, 246] }
    });

    doc.save(`Schools_Report_${district}_${state}.pdf`);
  };

  // Download as Excel
  const handleDownloadExcel = () => {
    const wsData = [
      ["School Name", "UDISE ID", "District", "School Type", "Total Pledges Taken", "Teachers Participated", "Students Participated"],
      ...schools.map(school => [
        school.school_name,
        school.udise_id || school.udiseCode,
        district,
        school.school_type || 'N/A',
        school.totalPledges || 0,
        school.teachersCount || 0,
        school.studentsCount || 0
      ])
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Schools");
    XLSX.writeFile(wb, `Schools_Report_${district}_${state}.xlsx`);
  };

  return (
    <>
      <HeaderAdmin />
      <section className="scholdashbrdSec superAdminDashboardSec">
        <Container fluid>
          <div className="dashbrdTopSrchDiv lavel mt-3 mb-4" style={{ position: "relative", zIndex: 999 }}>
            {theme === 'environment-day' ? (
              <>
                <h1 style={{ color: 'orange', fontWeight: 'bold' }}>ENVIRONMENT DAY 2026</h1>
                <h2 className="mb-2">School Wise Analytics</h2>
              </>
            ) : (
              <>
                <h1 style={{ color: 'orange', fontWeight: 'bold' }}>EK PED MAA KE NAAM 2.0</h1>
                <h2 className="mb-2">School-wise <span className="hdngBrkr"> Drill-down </span> Analytics</h2>
              </>
            )}
            <h3 style={{ letterSpacing: "0.05em", color: '#555' }}>
              District: <span className="text-success fw-bold">{district}</span> | State/UT/AB: <span className="text-success fw-bold">{state}</span>
            </h3>
            {theme !== 'environment-day' && (
              <h6 style={{ letterSpacing: "0.05em", color: '#be3441', maxWidth: '800px', margin: '10px 0' }}>
                The school data will sync once every 48 hours. Data is fetched from real-time records.
              </h6>
            )}

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button onClick={() => window.history.back()} className="btn-back">
                <IoMdArrowRoundBack size={20} className="me-1" /> Back
              </Button>
              <Button onClick={handleDownloadPDF} className="btn-pdf" disabled={schools.length === 0}>
                <FaDownload size={16} className="me-1" /> Download PDF
              </Button>
              <Button onClick={handleDownloadExcel} className="btn-excel" disabled={schools.length === 0}>
                <FaDownload size={16} className="me-1" /> Download Excel
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" />
              <p className="mt-2 text-muted">Fetching school-wise data...</p>
            </div>
          ) : error ? (
            <Alert variant="danger" className="text-center">{error}</Alert>
          ) : (
            <>
              <div className="custom-table-wrapper">
                <Table responsive hover className="custom-table">
                  <thead>
                    <tr>
                      <th>School Name</th>
                      <th>UDISE ID</th>
                      <th>District</th>
                      <th>School Type</th>
                      <th>Total Pledges</th>
                      <th>Teachers</th>
                      <th>Students</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="p-0">
                          <div className="empty-state-wrapper">
                            <FaInbox className="empty-state-icon" />
                            <h4>No Schools Found</h4>
                            <p>There is no participation data available for this district at the moment.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      schools.map((school, idx) => (
                        <tr key={school.udise_id || school.udiseCode || idx}>
                          <td data-label="School Name" className="fw-bold" style={{ color: '#2d5a3a' }}>{school.school_name}</td>
                          <td data-label="UDISE ID" className="fw-bold text-muted">{school.udise_id || school.udiseCode}</td>
                          <td data-label="District">{district}</td>
                          <td data-label="School Type">
                            <span className="school-type-badge">
                              {school.school_type || 'N/A'}
                            </span>
                          </td>
                          <td data-label="Total Pledges" className="numeric-cell">{school.totalPledges || 0}</td>
                          <td data-label="Teachers" className="numeric-cell text-primary">{school.teachersCount || 0}</td>
                          <td data-label="Students" className="numeric-cell text-info">{school.studentsCount || 0}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {schools.length > 0 && (
                <div className="paginationDiv d-flex justify-content-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </Container>
      </section>
    </>
  );
}

export default ViewSchools; 